import React, { useState, useEffect, useContext } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext, API } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  LayoutDashboard, 
  Plus,
  List,
  LogOut,
  Menu,
  X,
  Briefcase,
  Building2,
  BarChart3,
  AlertTriangle,
  CreditCard,
  CheckCircle,
  Zap,
  MapPin,
  Users
} from 'lucide-react';
import OrganizerExhibitionsList from '../components/organizer/OrganizerExhibitionsList';
import OrganizerNewExhibition from '../components/organizer/OrganizerNewExhibition';
import OrganizerExhibitionWizard from '../components/organizer/OrganizerExhibitionWizard';
import OrganizerLayoutEditor from '../components/organizer/OrganizerLayoutEditor';
import OrganizerCompanyList from '../components/organizer/OrganizerCompanyList';
import OrganizerSettings from '../components/organizer/OrganizerSettings';
import OrganizerReport from '../components/organizer/OrganizerReport';
import OrganizerTeamMembers from '../components/organizer/OrganizerTeamMembers';
import TenantDashboard from '../components/organizer/TenantDashboard';
import StandRequestsPanel from '../components/organizer/StandRequestsPanel';
import OrganizerContracts from './OrganizerContracts';
import ErrorBoundary from '../components/ErrorBoundary';

const menuItems = [
  { section: 'Genel', items: [
    { path: 'dashboard', label: 'Dashboard', icon: BarChart3 },
  ]},
  { section: 'Fuarlar', items: [
    { path: 'new-exhibition', label: 'Yeni Fuar Ekle', icon: Plus },
    { path: '', label: 'Tüm Fuarlarım', icon: List },
    { path: 'stand-requests', label: 'Stand Talepleri', icon: MapPin },
  ]},
  { section: 'Firmalar', items: [
    { path: 'companies/new', label: 'Yeni Firma Ekle', icon: Plus },
    { path: 'companies', label: 'Tüm Firmalar', icon: Building2 },
  ]},
  { section: 'Sistem', items: [
    { path: 'team', label: 'Kullanıcı Yönetimi', icon: Users },
    { path: 'settings', label: 'Ayarlar', icon: LayoutDashboard },
  ]},
];

export default function OrganizerDashboard() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [subStatus, setSubStatus] = useState(null);

  const currentPath = location.pathname.replace('/organizer/', '');

  useEffect(() => {
    const fetchSub = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/billing/subscription-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSubStatus(res.data);
      } catch {
        // Non-critical
      }
    };
    fetchSub();
  }, []);

  const isActive = subStatus?.subscription_status === 'active';
  const isTrialExpired = subStatus?.subscription_status === 'inactive';
  const isTrial = subStatus?.subscription_status === 'trial';
  const daysLeft = subStatus?.days_remaining ?? null;
  const totalDays = 14;
  const usedDays = daysLeft !== null ? Math.max(0, totalDays - daysLeft) : 0;
  const progressPct = daysLeft !== null ? Math.round((usedDays / totalDays) * 100) : 0;

  return (
    <div className="flex h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 flex-col">
      {/* Trial Expired Top Banner */}
      {isTrialExpired && (
        <div className="flex items-center justify-between px-4 py-2 text-sm bg-red-600 text-white"
          data-testid="trial-expired-banner">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            <span>Deneme süreniz dolmuştur. Devam etmek için abonelik satın alın.</span>
          </div>
          <button
            onClick={() => navigate('/pricing')}
            className="flex items-center gap-1.5 text-xs font-bold px-3 py-1 bg-white text-red-600 rounded-full hover:bg-red-50"
            data-testid="expired-upgrade-btn"
          >
            <CreditCard className="w-3 h-3" />
            Şimdi Abone Ol
          </button>
        </div>
      )}

      <div className="flex flex-1 overflow-hidden">
      {/* Sidebar */}
      <aside className={`${
        sidebarOpen ? 'w-64' : 'w-20'
      } bg-gradient-to-b from-teal-900 to-emerald-800 text-white transition-all duration-300 flex flex-col`}>
        {/* Header */}
        <div className="p-4 flex items-center justify-between border-b border-teal-700">
          {sidebarOpen && (
            <div className="flex items-center gap-2">
              <Briefcase className="w-6 h-6 text-teal-300" />
              <span className="font-bold text-lg">Organizatör</span>
            </div>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-white hover:bg-teal-700"
            data-testid="toggle-sidebar"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>

        {/* User info */}
        <div className="p-4 border-b border-teal-700">
          {sidebarOpen ? (
            <div>
              <p className="text-sm text-teal-300">Hoşgeldiniz</p>
              <p className="font-semibold truncate">{user?.name}</p>
              <p className="text-xs text-teal-300 truncate">{user?.email}</p>
            </div>
          ) : (
            <div className="w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center mx-auto">
              <span className="text-sm font-bold">{user?.name?.charAt(0)}</span>
            </div>
          )}
        </div>

        {/* Subscription Widget */}
        {sidebarOpen ? (
          <div className="mx-3 my-3 rounded-xl overflow-hidden border border-teal-700/50" data-testid="subscription-widget">
            {isActive ? (
              /* Paid subscription */
              <div className="bg-emerald-700/40 px-3 py-2.5">
                <div className="flex items-center gap-1.5">
                  <CheckCircle className="w-3.5 h-3.5 text-emerald-300 flex-shrink-0" />
                  <span className="text-xs font-bold text-emerald-200 uppercase tracking-wide">Ücretli Üyelik</span>
                </div>
                <p className="text-[10px] text-teal-300 mt-0.5">Tüm özellikler aktif</p>
              </div>
            ) : isTrial ? (
              /* Free trial */
              <div className="bg-teal-800/50 px-3 py-2.5 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <Zap className="w-3 h-3 text-amber-300 flex-shrink-0" />
                    <span className="text-[10px] font-bold text-amber-200 uppercase tracking-wide">Ücretsiz Abonelik</span>
                  </div>
                  {daysLeft !== null && (
                    <span className={`text-[10px] font-bold ${daysLeft <= 3 ? 'text-red-300' : 'text-teal-200'}`}>
                      {daysLeft} gün
                    </span>
                  )}
                </div>
                {/* Progress bar */}
                {daysLeft !== null && (
                  <div className="space-y-1">
                    <div className="h-1.5 bg-teal-700 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${progressPct > 80 ? 'bg-red-400' : progressPct > 50 ? 'bg-amber-400' : 'bg-emerald-400'}`}
                        style={{ width: `${progressPct}%` }}
                      />
                    </div>
                    <p className="text-[10px] text-teal-400">{usedDays}/{totalDays} gün kullanıldı</p>
                  </div>
                )}
                <button
                  onClick={() => navigate('/pricing')}
                  className="w-full text-[11px] font-bold bg-amber-400 hover:bg-amber-300 text-amber-900 rounded-lg py-1.5 transition-colors"
                  data-testid="sidebar-upgrade-btn"
                >
                  Şimdi Hemen Yükselt
                </button>
              </div>
            ) : isTrialExpired ? (
              /* Expired */
              <div className="bg-red-900/40 px-3 py-2.5 space-y-2">
                <div className="flex items-center gap-1.5">
                  <AlertTriangle className="w-3 h-3 text-red-300 flex-shrink-0" />
                  <span className="text-[10px] font-bold text-red-200 uppercase tracking-wide">Deneme Süresi Doldu</span>
                </div>
                <button
                  onClick={() => navigate('/pricing')}
                  className="w-full text-[11px] font-bold bg-red-500 hover:bg-red-400 text-white rounded-lg py-1.5 transition-colors"
                  data-testid="sidebar-expired-upgrade-btn"
                >
                  Abone Ol
                </button>
              </div>
            ) : null}
          </div>
        ) : (
          /* Collapsed sidebar - just an icon */
          <div className="flex justify-center py-2" data-testid="subscription-widget-collapsed">
            {isActive ? (
              <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center" title="Ücretli Üyelik">
                <CheckCircle className="w-4 h-4 text-white" />
              </div>
            ) : isTrial ? (
              <button
                onClick={() => navigate('/pricing')}
                className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center hover:bg-amber-400 transition-colors"
                title={`${daysLeft !== null ? daysLeft + ' gün kaldı' : 'Ücretsiz Deneme'}`}
                data-testid="sidebar-upgrade-btn-collapsed"
              >
                <Zap className="w-4 h-4 text-white" />
              </button>
            ) : (
              <button
                onClick={() => navigate('/pricing')}
                className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center"
                title="Abonelik Süresi Doldu"
              >
                <AlertTriangle className="w-4 h-4 text-white" />
              </button>
            )}
          </div>
        )}

        {/* Menu */}
        <ScrollArea className="flex-1 py-4">
          <nav className="space-y-1 px-2">
            {menuItems.map((section) => (
              <div key={section.section}>
                {sidebarOpen && (
                  <p className="text-xs font-semibold text-teal-400 uppercase tracking-wider px-3 py-2 mt-2">
                    {section.section}
                  </p>
                )}
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const basePath = item.path.split('/')[0];
                  const currentBase = currentPath.split('/')[0];
                  const isActiveMenu = item.path === '' 
                    ? currentPath === '' || currentPath === 'organizer'
                    : currentPath === item.path || (item.path !== '' && currentBase === basePath && !['new-exhibition', 'new'].includes(item.path));

                  return (
                    <button
                      key={item.path}
                      onClick={() => {
                        if (item.path === 'companies/new') {
                          navigate('/organizer/companies');
                          setTimeout(() => {
                            document.getElementById('new-company-trigger')?.click();
                          }, 300);
                        } else {
                          navigate(`/organizer/${item.path}`);
                        }
                      }}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                        isActiveMenu 
                          ? 'bg-teal-600 text-white' 
                          : 'text-teal-100 hover:bg-teal-700 hover:text-white'
                      }`}
                      data-testid={`menu-${item.path || 'list'}`}
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" />
                      {sidebarOpen && <span className="text-sm font-medium">{item.label}</span>}
                    </button>
                  );
                })}
              </div>
            ))}
          </nav>
        </ScrollArea>

        {/* Logout */}
        <div className="p-4 border-t border-teal-700">
          <Button
            onClick={logout}
            variant="ghost"
            className={`w-full text-red-300 hover:text-red-200 hover:bg-teal-700 ${
              sidebarOpen ? 'justify-start' : 'justify-center'
            }`}
            data-testid="organizer-logout-btn"
          >
            <LogOut className="w-5 h-5" />
            {sidebarOpen && <span className="ml-3">Çıkış Yap</span>}
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <ErrorBoundary>
          <Routes>
            <Route index element={<OrganizerExhibitionsList />} />
            <Route path="dashboard" element={<TenantDashboard />} />
            <Route path="new-exhibition" element={<OrganizerNewExhibition />} />
            <Route path="exhibition-wizard/:exhibitionId" element={<OrganizerExhibitionWizard />} />
            <Route path="edit/:exhibitionId" element={<OrganizerLayoutEditor />} />
            <Route path="report/:exhibitionId" element={<OrganizerReport />} />
            <Route path="companies" element={<OrganizerCompanyList />} />
            <Route path="companies/:filter" element={<OrganizerCompanyList />} />
            <Route path="team" element={<OrganizerTeamMembers />} />
            <Route path="stand-requests" element={<StandRequestsPanel />} />
            <Route path="contracts" element={<OrganizerContracts />} />
            <Route path="settings" element={<OrganizerSettings />} />
          </Routes>
        </ErrorBoundary>
      </main>
    </div>
    </div>
  );
}